import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import Notification from '../../../components/base/Notification';

interface ResearchFigureFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  editData?: any;
}

export default function ResearchFigureForm({ onClose, onSuccess, editData }: ResearchFigureFormProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState({ 
    title: false,
    category: false,
    file: false,
    purposeWhat: false,
    purposeWhy: false,
    axisLegend: false,
    keyPoints: false,
    findings: false,
    limitations: false,
    futureAnalysis: false,
    dataName: false,
    dataPeriod: false
  });
  const [formData, setFormData] = useState({
    title: '',
    category: '概念図',
    file: null as File | null,
    purpose_what: '',
    purpose_why: '',
    axis_legend: '',
    key_points: '',
    findings: '',
    limitations: '',
    future_analysis: '',
    data_name: '',
    data_period: '',
  });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [researchTheme, setResearchTheme] = useState('');
  const [availableThemes, setAvailableThemes] = useState<string[]>([]);

  const categories = ['概念図', 'データ構造', '記述統計', '推定結果'];

  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || '',
        category: editData.category || '概念図',
        file: null,
        purpose_what: editData.purpose_what || editData.purpose || '',
        purpose_why: editData.purpose_why || '',
        axis_legend: editData.axis_legend || editData.how_to_read || '',
        key_points: editData.key_points || '',
        findings: editData.findings || editData.tentative_interpretation || '',
        limitations: editData.limitations || '',
        future_analysis: editData.future_analysis || '',
        data_name: editData.data_name || editData.data_source || '',
        data_period: editData.data_period || '',
      });
      setResearchTheme(editData.research_theme || '');
    }
    fetchAvailableThemes();
  }, [editData]);

  const fetchAvailableThemes = async () => {
    try {
      const { data, error } = await supabase
        .from('research_overview')
        .select('theme')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const themes = data?.map(item => item.theme).filter(Boolean) || [];
      setAvailableThemes(themes);
    } catch (error) {
      console.error('テーマ取得エラー:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  const handleDelete = async () => {
    if (!editData?.id) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('research_figures')
        .delete()
        .eq('id', editData.id);

      if (error) throw error;

      setNotification({ type: 'success', message: '図表を削除しました' });
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('削除エラー:', error);
      setNotification({ type: 'error', message: '削除に失敗しました' });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setNotification({ type: 'error', message: 'ログインが必要です。再度ログインしてください。' });
      return;
    }

    if (!formData.title || !formData.purpose_what) {
      setNotification({ type: 'error', message: 'タイトルと目的は必須です' });
      return;
    }

    if (!editData && !formData.file) {
      setNotification({ type: 'error', message: 'ファイルは必須です' });
      return;
    }

    setLoading(true);

    try {
      let filePath = editData?.file || '';

      if (formData.file) {
        const fileName = `${Date.now()}_${formData.file.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('research-files')
          .upload(`figures/${fileName}`, formData.file);

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error(`ファイルアップロードエラー: ${uploadError.message}`);
        }
        filePath = uploadData.path;
      }

      const dataToSave = {
        user_id: user.id,
        title: formData.title,
        category: formData.category,
        file: filePath,
        research_theme: researchTheme || null,
        purpose_what: formData.purpose_what,
        purpose_why: formData.purpose_why || null,
        axis_legend: formData.axis_legend || null,
        key_points: formData.key_points || null,
        findings: formData.findings || null,
        limitations: formData.limitations || null,
        future_analysis: formData.future_analysis || null,
        data_name: formData.data_name || null,
        data_period: formData.data_period || null,
      };

      if (editData?.id) {
        const { error } = await supabase
          .from('research_figures')
          .update(dataToSave)
          .eq('id', editData.id);

        if (error) {
          console.error('UPDATE error:', error);
          throw new Error(`更新エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '図表を更新しました' });
      } else {
        const { error } = await supabase.from('research_figures').insert(dataToSave);

        if (error) {
          console.error('INSERT error:', error);
          throw new Error(`保存エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '図表を保存しました' });
      }

      setTimeout(() => {
        if (onSuccess) onSuccess();
        else onClose();
      }, 1500);
    } catch (err: any) {
      console.error('保存エラー詳細:', err);
      setNotification({ type: 'error', message: `保存に失敗しました: ${err?.message || '不明なエラー'}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 md:p-8">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex items-center justify-between mb-5 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
          <i className="ri-image-line text-amber-600"></i>
          {editData ? '図表を編集' : '図表を追加'}
        </h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
        {/* 基本情報 */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 sm:p-6 border-l-4 border-amber-500">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-information-line text-amber-600"></i>基本情報
          </h3>

          <div className="space-y-4">
            {/* 関連テーマ */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">関連テーマ</label>
              </div>
              <select
                value={researchTheme}
                onChange={(e) => setResearchTheme(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
              >
                <option value="">テーマを選択（任意）</option>
                {availableThemes.map((theme, index) => (
                  <option key={index} value={theme}>{theme}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">「テーマと目的」で登録したテーマを選択できます</p>
            </div>

            {/* タイトル */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">タイトル <span className="text-red-500">*</span></label>
                <button type="button" onClick={() => setShowHelp({ ...showHelp, title: !showHelp.title })} className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors cursor-pointer text-xs">?</button>
              </div>
              {showHelp.title && (
                <div className="text-xs text-slate-600 mb-2 bg-white p-3 rounded-lg border border-amber-200">
                  <div className="font-semibold mb-1">📌 何を書く？</div>
                  <p className="mb-1">図表の内容を端的に表すタイトルです。</p>
                  <div className="font-semibold mb-1">💡 記入例</div>
                  <p>「事業承継の意思決定モデル」「相続税負担と承継タイミングの関係」</p>
                </div>
              )}
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                placeholder="例：事業承継の意思決定モデル"
                required
              />
            </div>

            {/* カテゴリ */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">カテゴリ <span className="text-red-500">*</span></label>
              </div>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* ファイル */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">ファイル {!editData && <span className="text-red-500">*</span>}</label>
              </div>
              <input
                type="file"
                onChange={handleFileChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                accept=".png,.jpg,.jpeg,.pdf,.svg"
                required={!editData}
              />
              <p className="text-xs text-slate-500 mt-1">PNG、JPG、PDF、SVG形式のファイルをアップロード</p>
            </div>
          </div>
        </div>

        {/* 目的 */}
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 sm:p-6 border-l-4 border-blue-500">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-target-line text-blue-600"></i>目的
          </h3>

          <div className="space-y-4">
            {/* 何を示すための図表か */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">何を示すための図表か <span className="text-red-500">*</span></label>
              <textarea
                value={formData.purpose_what}
                onChange={(e) => setFormData({ ...formData, purpose_what: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                rows={2}
                placeholder="〇〇と△△の関係を示す"
                required
              />
            </div>

            {/* なぜこの図表が必要か */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">なぜこの図表が必要か（論文のどこで使うか）</label>
              <textarea
                value={formData.purpose_why}
                onChange={(e) => setFormData({ ...formData, purpose_why: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                rows={2}
                placeholder="〇〇セクションで△△を説明するために使用する"
              />
            </div>
          </div>
        </div>

        {/* 読み方 */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6 border-l-4 border-purple-500">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-eye-line text-purple-600"></i>読み方
          </h3>

          <div className="space-y-4">
            {/* 軸・凡例の説明 */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">軸・凡例の説明（横軸・縦軸・色分けなど）</label>
              <textarea
                value={formData.axis_legend}
                onChange={(e) => setFormData({ ...formData, axis_legend: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                rows={2}
                placeholder="横軸は〇〇、縦軸は△△を表す。色分けは□□を示す。"
              />
            </div>

            {/* 注目すべきポイント */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">注目すべきポイント</label>
              <textarea
                value={formData.key_points}
                onChange={(e) => setFormData({ ...formData, key_points: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base"
                rows={2}
                placeholder="〇〇の部分に注目。△△の傾向が見られる。"
              />
            </div>
          </div>
        </div>

        {/* 暫定解釈 */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 border-l-4 border-green-500">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-lightbulb-line text-green-600"></i>暫定解釈
          </h3>

          <div className="space-y-4">
            {/* この図表から読み取れること */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">この図表から読み取れること</label>
              <textarea
                value={formData.findings}
                onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                rows={3}
                placeholder="〇〇と△△の間に□□の関係が見られる。"
              />
            </div>

            {/* 留保・注意点 */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">留保・注意点（因果関係ではない等）</label>
              <textarea
                value={formData.limitations}
                onChange={(e) => setFormData({ ...formData, limitations: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                rows={2}
                placeholder="ただし〇〇は未検証。△△の可能性もある。"
              />
            </div>

            {/* 今後の追加分析の必要性 */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">今後の追加分析の必要性</label>
              <textarea
                value={formData.future_analysis}
                onChange={(e) => setFormData({ ...formData, future_analysis: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
                rows={2}
                placeholder="〇〇を確認するために△△の分析が必要。"
              />
            </div>
          </div>
        </div>

        {/* データ源 */}
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4 sm:p-6 border-l-4 border-slate-500">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <i className="ri-database-2-line text-slate-600"></i>データ源
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">データの名称・出典</label>
              <input
                type="text"
                value={formData.data_name}
                onChange={(e) => setFormData({ ...formData, data_name: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-base"
                placeholder="例：企業活動基本調査"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">対象期間・サンプルサイズ</label>
              <input
                type="text"
                value={formData.data_period}
                onChange={(e) => setFormData({ ...formData, data_period: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent text-base"
                placeholder="例：2010-2020年、N=5,432企業"
              />
            </div>
          </div>
        </div>

        {/* ボタン */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          {editData && (
            <button type="button" onClick={() => setShowDeleteConfirm(true)} className="w-full sm:w-auto px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer">
              削除
            </button>
          )}
          <button type="button" onClick={onClose} className="w-full sm:flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer">
            キャンセル
          </button>
          <button type="submit" disabled={loading} className="w-full sm:flex-1 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
            {loading ? '保存中...' : editData ? '更新する' : '保存'}
          </button>
        </div>
      </form>

      {/* 削除確認モーダル */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 sm:p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-slate-900 mb-2">削除の確認</h3>
            <p className="text-slate-600 mb-6">この図表を削除してもよろしいですか？この操作は取り消せません。</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="w-full sm:flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer">
                キャンセル
              </button>
              <button onClick={handleDelete} disabled={loading} className="w-full sm:flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
                {loading ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
